import type { ExpenseRecord, IncomeRecord, Transaction } from "../types";
import { getDisplayText } from "./display";

export const SIMILAR_NAME_CHIP_LIMIT = 5;
export const SIMILAR_NAME_FILTER_KEY = "nameToken";

export const CLIENT_ONLY_TABLE_FILTER_KEYS = new Set([
  SIMILAR_NAME_FILTER_KEY,
]);

/** Common Thai name / shop prefixes, longest first so นางสาว wins over นาง. */
const NAME_GROUP_PREFIXES = [
  "นางสาว",
  "บริษัท",
  "ห้าง",
  "หจก",
  "ร้าน",
  "นาย",
  "นาง",
  "คุณ",
  "น้อง",
  "พี่",
  "ลุง",
  "ป้า",
  "น้า",
  "อา",
  "แม่",
  "พ่อ",
  "ตา",
  "ยาย",
  "ปู่",
  "ย่า",
  "เฮีย",
  "เจ๊",
  "ครู",
  "หมอ",
  "ดร.",
  "ดร",
];

const PLACEHOLDER_NAMES = new Set([
  "",
  "-",
  "—",
  "ไม่ระบุ",
  "ไม่ระบุชื่อ",
  "ไม่มีชื่อ",
]);

type NameChipCandidate = {
  token: string;
  rows: Set<number>;
  distinct: Set<string>;
  honorific: boolean;
};

function normalizeName(value: string): string {
  return value.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
}

function isPlaceholderName(name: string): boolean {
  return PLACEHOLDER_NAMES.has(normalizeName(name));
}

function isNoisyToken(token: string): boolean {
  if (token.length < 2) return true;
  if (token.includes("฿")) return true;
  return /^[\d.,]+$/.test(token);
}

function longestCommonPrefix(left: string, right: string): string {
  const max = Math.min(left.length, right.length);
  let index = 0;
  while (index < max && left[index] === right[index]) index += 1;
  return left.slice(0, index);
}

function addCandidate(
  byToken: Map<string, NameChipCandidate>,
  token: string,
  row: number,
  label: string,
  honorific: boolean,
) {
  const normalized = normalizeName(token);
  if (isNoisyToken(normalized) || isPlaceholderName(normalized)) return;

  let candidate = byToken.get(normalized);
  if (!candidate) {
    candidate = {
      token: normalized,
      rows: new Set(),
      distinct: new Set(),
      honorific,
    };
    byToken.set(normalized, candidate);
  } else if (honorific) {
    candidate.honorific = true;
  }
  candidate.rows.add(row);
  candidate.distinct.add(label);
}

function nameHasPrefix(name: string, prefix: string): boolean {
  if (name.startsWith(prefix)) return true;
  return name.split(" ").some((part) => part.startsWith(prefix));
}

/**
 * Build up to `limit` quick-filter chips from names that repeat or share a
 * prefix such as ลุง / พี่ / ร้าน.
 */
export function buildSimilarNameChips(
  names: Array<string | null | undefined>,
  limit = SIMILAR_NAME_CHIP_LIMIT,
): string[] {
  const labels = names
    .map((name) => normalizeName(name ?? ""))
    .filter((name) => !isPlaceholderName(name));
  if (labels.length < 2) return [];

  const byToken = new Map<string, NameChipCandidate>();

  for (let row = 0; row < labels.length; row += 1) {
    const label = labels[row];
    addCandidate(byToken, label, row, label, false);

    for (const part of label.split(/[\s,./|+\-_]+/).filter(Boolean)) {
      if (part !== label) addCandidate(byToken, part, row, label, false);
    }

    for (const prefix of NAME_GROUP_PREFIXES) {
      const normalizedPrefix = normalizeName(prefix);
      if (nameHasPrefix(label, normalizedPrefix)) {
        addCandidate(byToken, prefix, row, label, true);
      }
    }
  }

  const unique = [...new Set(labels)];
  const grouped = new Map<string, string[]>();
  for (const name of unique) {
    const key = name.slice(0, 2);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(name);
    else grouped.set(key, [name]);
  }

  for (const bucket of grouped.values()) {
    if (bucket.length < 2) continue;
    for (let i = 0; i < bucket.length; i += 1) {
      for (let j = i + 1; j < bucket.length; j += 1) {
        const shared = longestCommonPrefix(bucket[i], bucket[j]);
        if (shared.length < 3) continue;
        for (let row = 0; row < labels.length; row += 1) {
          if (labels[row].startsWith(shared)) {
            addCandidate(byToken, shared, row, labels[row], false);
          }
        }
      }
    }
  }

  const candidates = [...byToken.values()].filter(
    (candidate) => candidate.rows.size >= 2,
  );

  candidates.sort((left, right) => {
    if (right.rows.size !== left.rows.size) {
      return right.rows.size - left.rows.size;
    }

    const leftMulti = left.distinct.size > 1 ? 1 : 0;
    const rightMulti = right.distinct.size > 1 ? 1 : 0;
    if (leftMulti !== rightMulti) return rightMulti - leftMulti;

    if (leftMulti) {
      if (left.honorific !== right.honorific) return left.honorific ? -1 : 1;
      if (left.token.length !== right.token.length) {
        return left.token.length - right.token.length;
      }
    } else if (left.token.length !== right.token.length) {
      return right.token.length - left.token.length;
    }

    if (left.honorific !== right.honorific) return left.honorific ? -1 : 1;
    return left.token.localeCompare(right.token, "th");
  });

  const picked: NameChipCandidate[] = [];
  for (const candidate of candidates) {
    if (picked.length >= limit) break;

    const redundant = picked.some((previous) => {
      if (previous.rows.size !== candidate.rows.size) return false;
      for (const row of candidate.rows) {
        if (!previous.rows.has(row)) return false;
      }
      return true;
    });
    if (redundant) continue;

    picked.push(candidate);
  }

  return picked.map((candidate) => {
    const fromDictionary = NAME_GROUP_PREFIXES.find(
      (prefix) => normalizeName(prefix) === candidate.token,
    );
    return fromDictionary ?? candidate.token;
  });
}

export function nameMatchesToken(
  name: string | null | undefined,
  token: string | null | undefined,
): boolean {
  const normalizedName = normalizeName(name ?? "");
  const normalizedToken = normalizeName(token ?? "");
  if (!normalizedName || !normalizedToken) return false;
  return normalizedName.includes(normalizedToken);
}

export function getLedgerNameLabel(
  record: Transaction,
  workerId: string,
): string {
  if (workerId === "bill-lin-expense") {
    return getDisplayText((record as ExpenseRecord).vendor).trim();
  }

  const income = record as IncomeRecord;
  return (
    getDisplayText(income.party_name).trim() ||
    getDisplayText(income.customer_name).trim() ||
    getDisplayText(income.sender_name).trim()
  );
}
